import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useMapaDemanda } from '../hooks/useMapaDemanda';

// Configurar ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-shadow.png',
});

// Componente de fallback
const MapaFallback = ({ height, mensagem = "Mapa temporariamente indisponível", onRetry }) => (
  <div 
    className="mapa-fallback"
    style={{ height }}
  >
    <div style={{ textAlign: 'center' }}>
      <i className="fas fa-map-marker-alt" style={{ fontSize: '24px', marginBottom: '8px', display: 'block' }}></i>
      <div style={{ marginBottom: '10px' }}>{mensagem}</div>
      {onRetry && (
        <button 
          onClick={onRetry}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Tentar Novamente
        </button>
      )}
    </div>
  </div>
);

// Componente de carregamento
const MapaCarregando = ({ height }) => (
  <div 
    className="mapa-loading"
    style={{ height }}
  >
    <div className="mapa-loading-spinner"></div>
    <div>Carregando mapa...</div>
  </div>
);

/**
 * Componente de mapa pequeno para mostrar a localização da demanda
 */
export default function MapaDemanda({ demanda, height = '200px', zoom: zoomProp }) {
  const { 
    coordenadas, 
    zoom, 
    erro, 
    carregando, 
    handleMapError, 
    resetar,
    temCoordenadas 
  } = useMapaDemanda(demanda);
  
  const [mapKey, setMapKey] = useState(0);

  // Usar zoom personalizado se fornecido
  const zoomFinal = zoomProp || zoom;

  // Forçar re-render do mapa quando a demanda mudar
  useEffect(() => {
    setMapKey(prev => prev + 1);
  }, [demanda?.id]);

  // Estados de carregamento e erro
  if (carregando) {
    return <MapaCarregando height={height} />;
  }

  if (erro) {
    return (
      <MapaFallback 
        height={height} 
        mensagem={erro} 
        onRetry={resetar}
      />
    );
  }

  try {
    return (
      <div className="mapa-demanda-container" style={{ height }}>
        <MapContainer
          key={mapKey}
          center={coordenadas}
          zoom={zoomFinal}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          dragging={false}
          touchZoom={false}
          boxZoom={false}
          keyboard={false}
          whenCreated={(map) => {
            // Garantir que o mapa seja redimensionado corretamente
            setTimeout(() => {
              map.invalidateSize();
            }, 100);
          }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            errorTileUrl="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
            onError={(error) => handleMapError(error)}
          />
          <Marker position={coordenadas} />
        </MapContainer>
        
        {/* Indicador de localização */}
        {!temCoordenadas && (
          <div style={{
            position: 'absolute',
            bottom: '8px',
            left: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#666',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
          }}>
            Localização aproximada
          </div>
        )}
      </div>
    );
  } catch (error) {
    console.error('MapaDemanda - Erro ao renderizar mapa:', error);
    return (
      <MapaFallback 
        height={height} 
        mensagem="Erro ao renderizar mapa" 
        onRetry={resetar}
      />
    );
  }
}